import React, { useEffect, useRef, useState } from "react";
import { assetUrl } from "../api/http";

type LicensePlateCustomizationSelection = {
  imageUrl?: string;
  scale?: number;
  rotation?: number;
  offsetXPct?: number;
  offsetYPct?: number;
  text?: string;
  textFont?: string;
  textSizePx?: number;
  textOffsetXPct?: number;
  textOffsetYPct?: number;
  textColor?: string;
  textWeight?: number;
  textItalic?: boolean;
  textStroke?: boolean;
  textUppercase?: boolean;
  textLetterSpacingPx?: number;
};

type PreviewProps = {
  value: LicensePlateCustomizationSelection | null;
};

const PLATE_WIDTH_IN = 12;
const PLATE_HEIGHT_IN = 6;
const HOLE_DIAMETER_IN = 0.33;
const HOLE_RADIUS_IN = HOLE_DIAMETER_IN / 2;
const HOLE_CENTER_Y_IN = 0.72;
const HOLE_LEFT_X_IN = 2.5;
const HOLE_RIGHT_X_IN = 9.5;
const TEXT_RENDER_BASE_PRINT_HEIGHT = 240;

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

function ensureCanvasSize(canvas: HTMLCanvasElement, w: number, h: number) {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const pxW = Math.max(1, Math.round(w * dpr));
  const pxH = Math.max(1, Math.round(h * dpr));
  if (canvas.width !== pxW || canvas.height !== pxH) {
    canvas.width = pxW;
    canvas.height = pxH;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

function getLayout(w: number, h: number) {
  const plateRatio = PLATE_WIDTH_IN / PLATE_HEIGHT_IN;
  const plateW = Math.min(w * 0.92, h * 0.86 * plateRatio);
  const plateH = plateW / plateRatio;
  const plateX = (w - plateW) / 2;
  const plateY = (h - plateH) / 2;

  return {
    plateX,
    plateY,
    plateW,
    plateH,
    printX: plateX,
    printY: plateY,
    printW: plateW,
    printH: plateH,
    holeLeftX: plateX + (HOLE_LEFT_X_IN / PLATE_WIDTH_IN) * plateW,
    holeRightX: plateX + (HOLE_RIGHT_X_IN / PLATE_WIDTH_IN) * plateW,
    holeY: plateY + (HOLE_CENTER_Y_IN / PLATE_HEIGHT_IN) * plateH,
    holeRadius: (HOLE_RADIUS_IN / PLATE_HEIGHT_IN) * plateH,
  };
}

function drawPlate(
  ctx: CanvasRenderingContext2D,
  bounds: { w: number; h: number },
  loadedImage: HTMLImageElement | null,
  selection: LicensePlateCustomizationSelection
) {
  const { w, h } = bounds;
  const layout = getLayout(w, h);

  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(layout.plateX, layout.plateY, layout.plateW, layout.plateH);
  ctx.strokeStyle = "#9ca3af";
  ctx.lineWidth = Math.max(1, layout.plateH * 0.012);
  ctx.strokeRect(layout.plateX, layout.plateY, layout.plateW, layout.plateH);

  ctx.save();
  ctx.beginPath();
  ctx.rect(layout.printX, layout.printY, layout.printW, layout.printH);
  ctx.clip();

  if (loadedImage) {
    const baseScale = Math.max(
      layout.printW / loadedImage.width,
      layout.printH / loadedImage.height
    );
    const drawW = loadedImage.width * baseScale;
    const drawH = loadedImage.height * baseScale;
    const shiftX = ((selection.offsetXPct || 0) / 100) * layout.printW * 0.9;
    const shiftY = ((selection.offsetYPct || 0) / 100) * layout.printH * 0.9;
    ctx.save();
    ctx.translate(
      layout.printX + layout.printW / 2 + shiftX,
      layout.printY + layout.printH / 2 + shiftY
    );
    ctx.rotate(((selection.rotation || 0) * Math.PI) / 180);
    ctx.scale(selection.scale || 1, selection.scale || 1);
    ctx.drawImage(loadedImage, -drawW / 2, -drawH / 2, drawW, drawH);
    ctx.restore();
  }

  const text = selection.text || "";
  const styledText = selection.textUppercase ? text.toUpperCase() : text;
  if (styledText.trim()) {
    const authored = clamp(Number(selection.textSizePx || 78), 12, 280);
    const textSize = clamp(
      authored * (layout.printH / TEXT_RENDER_BASE_PRINT_HEIGHT),
      8,
      layout.printH * 0.78
    );
    const weight = clamp(Number(selection.textWeight || 900), 100, 900);
    const italic = selection.textItalic ? "italic " : "";
    const family = selection.textFont || "Impact";
    const authoredLetterSpacing = Number(selection.textLetterSpacingPx || 0);
    const letterSpacing = authoredLetterSpacing * (layout.printH / TEXT_RENDER_BASE_PRINT_HEIGHT);

    ctx.save();
    ctx.fillStyle = selection.textColor || "#111827";
    ctx.font = `${italic}${weight} ${Math.round(textSize)}px "${family}"`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const centerX =
      layout.printX +
      layout.printW / 2 +
      ((selection.textOffsetXPct || 0) / 100) * layout.printW * 0.9;
    const centerY =
      layout.printY +
      layout.printH / 2 +
      ((selection.textOffsetYPct || 0) / 100) * layout.printH * 0.9;

    if (selection.textStroke) {
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = Math.max(1, textSize * 0.09);
      ctx.strokeText(styledText, centerX, centerY);
    }

    if (Math.abs(letterSpacing) < 0.1) {
      ctx.fillText(styledText, centerX, centerY);
    } else {
      const measure = ctx.measureText(styledText);
      const totalWidth = measure.width + Math.max(0, styledText.length - 1) * letterSpacing;
      let x = centerX - totalWidth / 2;
      for (const ch of styledText) {
        const m = ctx.measureText(ch);
        const charW = m.width;
        ctx.fillText(ch, x + charW / 2, centerY);
        x += charW + letterSpacing;
      }
    }
    ctx.restore();
  }

  ctx.save();
  ctx.globalCompositeOperation = "destination-out";
  ctx.beginPath();
  ctx.arc(layout.holeLeftX, layout.holeY, layout.holeRadius * 1.08, 0, Math.PI * 2);
  ctx.arc(layout.holeRightX, layout.holeY, layout.holeRadius * 1.08, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  ctx.restore();

  ctx.beginPath();
  ctx.arc(layout.holeLeftX, layout.holeY, layout.holeRadius * 1.08, 0, Math.PI * 2);
  ctx.arc(layout.holeRightX, layout.holeY, layout.holeRadius * 1.08, 0, Math.PI * 2);
  ctx.fillStyle = "#f8fafc";
  ctx.fill();
}

export function LicensePlatePreview({ value }: PreviewProps) {
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [resizeTick, setResizeTick] = useState(0);

  useEffect(() => {
    const imageUrl = value?.imageUrl || "";
    if (!imageUrl) {
      setLoadedImage(null);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (!cancelled) setLoadedImage(img);
    };
    img.onerror = () => {
      if (!cancelled) setLoadedImage(null);
    };

    img.src = assetUrl(imageUrl) || imageUrl;
    return () => {
      cancelled = true;
    };
  }, [value?.imageUrl]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(() => setResizeTick((n) => n + 1));
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const bounds = canvas.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    const ctx = ensureCanvasSize(canvas, bounds.width, bounds.height);
    if (!ctx) return;
    drawPlate(ctx, { w: bounds.width, h: bounds.height }, loadedImage, value || {});
  }, [loadedImage, resizeTick, value]);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
    </div>
  );
}
